// @ts-strict-ignore
import pick from 'lodash/pick';
import omit from 'lodash/omit';
import {RCATriggerRequest, RCATriggerResult} from 'webapp/app_api/projects/rca/types';

import {purgeFetchCachePathsMatchingRegex} from 'common/report/queries/utils/fetch-cache';
import {i18next} from 'common/i18n';
import {apiClient} from 'common/util/api-client';
import {GlobalAccessParamName} from 'common/report/util/saved-entities/types';
import {ContentDetails} from 'common/report/util/dashboards/utils';
import {FetchOptions, SuccessResponse} from 'common/util/shared-client';
import {ContentActionType, ContentType, Dashboard} from 'common/report/dashboards/types';
import {
  addNewCellToRow,
  getLayoutParamWithContentRemoved,
  getLayoutUpdateParams,
  MAX_CELL_COL_SPAN,
} from 'common/report/util/dashboards/grid-layout';
import {DashboardDigest} from 'common/query-builder/query-report-header/subscribe-to-boards-menu/types';

import {appApiUrl} from '../utils';
import {BaseClient, QueryOptions} from '../utils/base-client';
import {NamedError} from '../utils/named-error';
import {DashboardCardWithAction} from './dashboard-types';

class FieldStringTooLongError extends NamedError {
  constructor(
    message,
    public fieldName: string,
    public fieldLength: string,
    public maxLength: string,
  ) {
    super(message);
  }

  static get type() {
    return `FieldStringTooLong`;
  }
}

class PermissionDeniedError extends NamedError {
  static get type() {
    return `PermissionDenied`;
  }
}

class NotImplementedError extends NamedError {
  static get type() {
    return `NotImplemented`;
  }
}

class InvalidParamsError extends NamedError {
  static get type() {
    return `InvalidParams`;
  }
}

class DashboardDoesNotExistError extends NamedError {
  static get type() {
    return `DashboardDoesNotExist`;
  }
}

class CardDoesNotExistError extends NamedError {
  static get type() {
    return `CardDoesNotExist`;
  }
}

class CardOrderOutOfSyncError extends NamedError {
  static get type() {
    return `CardOrderOutOfSync`;
  }
}

class BookmarkDoesNotExistError extends NamedError {
  static get type() {
    return `BookmarkDoesNotExist`;
  }
}

type ProjectId = string | number;

// TODO: refactor to use use xId rather than xID
interface BuildEndpointParams {
  projectId?: ProjectId;
  dashboardID?: number;
  bookmarkID?: number;
  erf?: boolean;
  emailDigest?: boolean;
  workspaceId?: number;
  favorites?: boolean;
  pin?: boolean;
  reportLinkId?: string;
}

export interface DashboardFields extends Partial<Pick<Dashboard, `title` | `description` | `filters` | `time_filter`>> {
  cards?: DashboardCardWithAction[];
}

export interface RCASourceData {
  timestamp?: string;
  type?: string;
  date?: string;
  metricIndex?: number;
  metric_source?: boolean;
}

const VALID_DASHBOARD_PARAMS = [
  `title`,
  `description`,
  `time_filter`,
  `filters`,
  `card_order`,
  `cards`,
  `duplicate`,
  `target_project_id`,
  `target_parent_dashboard_id`,
  GlobalAccessParamName,
];

/* Dashboards 2.0 API is documented here: https://github.com/mixpanel/analytics/blob/master/webapp/app_api/projects/dashboards/README.md */
class DashClient extends BaseClient {
  arrayFormatBracket = true;

  buildEndpoint({
    projectId,
    dashboardID,
    bookmarkID,
    erf,
    emailDigest,
    workspaceId,
    favorites,
    pin,
    reportLinkId,
  }: BuildEndpointParams) {
    let endpoint;
    if (dashboardID) {
      endpoint = `dashboards/${dashboardID}/`;
      if (erf) {
        endpoint += `erf`;
      } else if (favorites) {
        endpoint += `favorites/`;
      } else if (pin) {
        endpoint += `pin/`;
      } else if (reportLinkId) {
        endpoint += `report-links/${reportLinkId}`;
      } else {
        endpoint += ``;
      }
    } else if (bookmarkID) {
      endpoint = `dashboards/bookmarks/${bookmarkID}/dashboard-ids/`;
    } else if (emailDigest) {
      endpoint = `dashboard-reports/email-digests`;
    } else {
      endpoint = `dashboards/`;
    }
    return appApiUrl(endpoint, {projectId, workspaceId});
  }

  buildQueryParams(params) {
    const dashboardIds = params.dashboardIds ?? [];
    return {id: dashboardIds};
  }

  getExtraFetchOptions() {
    return {};
  }

  getFetchOptions(params: any, queryOpts: QueryOptions): FetchOptions {
    return super.getFetchOptions(params, {...queryOpts, ...this.getExtraFetchOptions()});
  }

  /** Sends a HTTP GET request to retrieve all dashboards for a specified project */
  queryAll(
    {
      projectId,
      dashboardIds,
      workspaceId,
    }: {
      projectId?: ProjectId;
      dashboardIds?: number[];
      workspaceId?: number;
    },
    queryOpts?: QueryOptions,
  ): {cached: Promise<Dashboard[] | null>; network: Promise<Dashboard[]>} {
    const params = {projectId, workspaceId, dashboardIds};
    return {
      cached: this.query(params, {...queryOpts, fromFetchCache: true}),
      network: this.query(params, {...queryOpts, updateFetchCache: true}),
    };
  }

  /** Sends a HTTP GET request to retrieve a specific dashboard */
  querySingle(
    {
      projectId,
      dashboardID,
      workspaceId,
    }: {
      projectId: ProjectId;
      dashboardID: number;
      workspaceId?: number;
    },
    queryOpts?: QueryOptions,
  ): {cached: Promise<Dashboard | null>; network: Promise<Dashboard>} {
    if (!projectId || !dashboardID) {
      throw new Error(`projectId and dashboardID are required`);
    }

    const params = {projectId, dashboardID, workspaceId};
    return {
      cached: this.query(params, {...queryOpts, fromFetchCache: true}),
      network: this.query(params, {...queryOpts, updateFetchCache: true}),
    };
  }

  /** Sends a HTTP GET request to retrieve a specific dashboard for ERF */
  querySingleERF(
    {
      projectId,
      dashboardID,
      workspaceId,
    }: {
      projectId: ProjectId;
      dashboardID: number;
      workspaceId?: number;
    },
    queryOpts?: QueryOptions,
  ): Promise<any> {
    return this.query({projectId, dashboardID, workspaceId, erf: true}, queryOpts);
  }

  queryEmailDigests(
    {projectId, workspaceId}: {projectId: ProjectId; workspaceId?: number},
    queryOpts?: QueryOptions,
  ): Promise<DashboardDigest[]> {
    return this.query({projectId, workspaceId, emailDigest: true}, queryOpts);
  }

  /** Sends a HTTP GET request to retrieve dashboards containing cards cards with target bookmark */
  queryBookmarkDashboardIds(
    {
      projectId,
      bookmarkID,
      workspaceId,
    }: {
      projectId: ProjectId;
      bookmarkID: number;
      workspaceId?: number;
    },
    queryOpts?: QueryOptions,
  ): Promise<number[]> {
    return this.query({projectId, bookmarkID, workspaceId}, queryOpts);
  }

  /** Sends a HTTP PATCH request to update the fields of a dashboard */
  async updateDashboardFields({
    projectId,
    dashboardID,
    fields = {},
    workspaceId,
  }: {
    projectId: ProjectId;
    dashboardID: number;
    fields: TODO;
    workspaceId?: number;
  }): Promise<any> {
    const url = this.buildEndpoint({projectId, dashboardID, workspaceId});
    const fetchOptions = this.getExtraFetchOptions();
    const result = await this.processResponse(await this.wrapErrorThrower(apiClient.patch(url, fields, fetchOptions)));
    await this.invalidateFetchCache();
    return result;
  }

  /** Sends a HTTP POST request to create a new dashboard */
  async postCreateDashboard({
    projectId,
    fields = {},
    workspaceId,
  }: {
    projectId: ProjectId;
    fields: DashboardFields;
    workspaceId?: number;
  }): Promise<Dashboard> {
    const postData: Partial<DashboardFields> = pick(fields, VALID_DASHBOARD_PARAMS);
    const url = this.buildEndpoint({projectId, workspaceId});
    const fetchOptions = this.getExtraFetchOptions();
    const result = await this.processResponse(await this.wrapErrorThrower(apiClient.post(url, postData, fetchOptions)));
    await this.invalidateFetchCache();
    return result;
  }
  /** Sends a HTTP POST request to create a root cause analaysis dashboard */
  async postCreateRCADashboard({
    projectId,
    rcaSourceId,
    rcaSourceData,
    rcaEventName,
    workspaceId,
    metricSource = false,
  }: {
    projectId: ProjectId;
    rcaSourceId?: number;
    rcaSourceData?: RCASourceData;
    rcaEventName?: string;
    workspaceId?: number;
    metricSource?: boolean;
  }): Promise<Dashboard> {
    const url = `${this.buildEndpoint({workspaceId, projectId})}create-rca-dashboard/`;
    // Forward the current UI language so the board title, description, and intro
    // text cards render in it (see webapp/rca/board_strings).
    const locale = i18next.language;
    // If we have the event name, use event name instead of the source id (which is likely null)
    const payload = rcaEventName
      ? {
          rca_event_name: rcaEventName,
          rca_source_data: rcaSourceData,
          metric_source: metricSource,
          locale,
        }
      : {
          rca_source_id: rcaSourceId,
          rca_source_data: rcaSourceData,
          metric_source: metricSource,
          locale,
        };

    const result = await this.processResponse(await this.wrapErrorThrower(apiClient.post(url, payload)));
    await this.invalidateFetchCache();
    return result.dashboard;
  }

  /** Triggers an AI-driven RCA analysis and returns the dashboard ID and task ID.
   *  Caller must provide exactly one source: `bookmarkId`, `bookmarkUrlSlug`, or a
   *  Lexicon Data-Volume-Monitoring ("issues-hub") source (`eventName` or `metricId`).
   */
  async postTriggerRCA({
    projectId,
    bookmarkId,
    bookmarkUrlSlug,
    eventName,
    metricId,
    rcaDate,
    changeDescription,
    metricIndex,
  }: {
    projectId: ProjectId;
    bookmarkId?: number;
    bookmarkUrlSlug?: string;
    eventName?: string;
    metricId?: number;
    rcaDate: string;
    changeDescription?: string;
    metricIndex?: number;
  }): Promise<RCATriggerResult> {
    const url = appApiUrl(`rca/trigger`, {projectId});
    const payload: RCATriggerRequest = {
      rca_date: rcaDate,
      change_description: changeDescription || ``,
      // Forward the current UI language so the RCA agent responds in it.
      locale: i18next.language,
    };
    if (bookmarkUrlSlug) {
      payload.bookmark_url_slug = bookmarkUrlSlug;
    } else if (bookmarkId) {
      payload.bookmark_id = bookmarkId;
    } else if (metricId) {
      payload.metric_id = metricId;
    } else if (eventName) {
      payload.event_name = eventName;
    }
    if (metricIndex !== undefined) {
      payload.metric_index = metricIndex;
    }
    const result = await this.processResponse(await this.wrapErrorThrower(apiClient.post(url, payload)));
    return result;
  }

  /**
   * Polls the RCA task status. Pass `taskId` (in-session) or `dashboardId`
   * (cold-mount resume: the backend resolves it to the board's in-flight task
   * and echoes its `task_id`, or null when the board has no RCA running).
   */
  async getRCAStatus({
    projectId,
    taskId,
    dashboardId,
    offset = 0,
  }: {
    projectId: ProjectId;
    taskId?: string;
    dashboardId?: number;
    offset?: number;
  }): Promise<{
    status: string;
    messages: Array<{type: string; content: string; ts: number}>;
    error: string | null;
    task_id: string | null;
  }> {
    const url = appApiUrl(`rca/status`, {projectId});
    const params: {offset: number; task_id?: string; dashboard_id?: number} = {offset};
    if (taskId !== undefined) {
      params.task_id = taskId;
    }
    if (dashboardId !== undefined) {
      params.dashboard_id = dashboardId;
    }
    return apiClient.get(url, params);
  }

  /** Sends a HTTP DELETE request to remove a dashboard */
  async deleteDashboard({
    projectId,
    dashboardID,
    workspaceId,
  }: {
    projectId: ProjectId;
    dashboardID: number;
    workspaceId?: number;
  }): Promise<Dashboard> {
    const url = this.buildEndpoint({projectId, dashboardID, workspaceId});
    const fetchOptions = this.getExtraFetchOptions();
    const result = await this.processResponse(await this.wrapErrorThrower(apiClient.delete(url, fetchOptions)));
    await this.invalidateFetchCache();
    return result;
  }

  /** Finalizes a Copilot draft dashboard by flipping `is_draft` to false. */
  async finalizeCopilotDraftDashboard({
    projectId,
    dashboardID,
    workspaceId,
    savedBy,
  }: {
    projectId: ProjectId;
    dashboardID: number;
    workspaceId?: number;
    savedBy: `copilot` | `user`;
  }): Promise<Dashboard> {
    const url = `${this.buildEndpoint({projectId, dashboardID, workspaceId})}copilot-finalize/`;
    const fetchOptions = this.getExtraFetchOptions();
    const result = await this.processResponse(
      await this.wrapErrorThrower(apiClient.patch(url, {saved_by: savedBy}, fetchOptions)),
    );
    await this.invalidateFetchCache();
    return result;
  }

  /** Sends an HTTP PATCH request to restore a dashboard */
  async undeleteDashboard({
    dashboardId,
    projectId,
    workspaceId,
  }: {
    dashboardId: number;
    projectId: string | number;
    workspaceId?: number;
  }): Promise<Dashboard> {
    const url = this.buildEndpoint({projectId, dashboardID: dashboardId, workspaceId});
    const fetchOptions = this.getExtraFetchOptions();
    const result = await this.processResponse(
      await this.wrapErrorThrower(apiClient.patch(url, {undelete: true}, fetchOptions)),
    );
    await this.invalidateFetchCache();
    return result;
  }

  /** Sends a HTTP POST request to remove multiple dashboards */
  async deleteDashboards({
    projectId,
    dashboardIds,
    workspaceId,
  }: {
    projectId: ProjectId;
    dashboardIds: number[];
    workspaceId?: number;
  }): Promise<SuccessResponse<string>> {
    const url = `${this.buildEndpoint({projectId, workspaceId})}bulk-delete`;
    return this.processResponse(await apiClient.post(url, {dashboard_ids: dashboardIds}));
  }

  /** Marks dashboard as favorite */
  async favoriteDashboard({
    projectId,
    dashboardID,
    workspaceId,
    isFavorited,
  }: {
    projectId: ProjectId;
    dashboardID: number;
    workspaceId?: number;
    isFavorited: boolean;
  }) {
    const url = this.buildEndpoint({projectId, dashboardID, workspaceId, favorites: true});
    const result = await (isFavorited ? apiClient.post(url, {}) : apiClient.delete(url));
    await this.invalidateFetchCache();
    return result;
  }

  /** Pin dashboard to a project */
  async pinDashboard({
    projectId,
    dashboardID,
    workspaceId,
    isPinned,
  }: {
    projectId: ProjectId;
    dashboardID: number;
    workspaceId?: number;
    isPinned: boolean;
  }) {
    const url = this.buildEndpoint({projectId, dashboardID, workspaceId, pin: true});
    const result = await (isPinned ? apiClient.post(url, {}) : apiClient.delete(url));
    await this.invalidateFetchCache();
    return result;
  }

  async wrapErrorThrower(promise: Promise<any>) {
    try {
      return await promise;
    } catch (error) {
      if (error.name === `ResponseError`) {
        const errorInfo = await error.response.json();
        this.throwApiError(errorInfo);
      } else {
        throw error;
      }
    }
  }

  throwApiError(apiResponse) {
    switch (apiResponse.type) {
      case FieldStringTooLongError.type:
        throw new FieldStringTooLongError(
          apiResponse.error,
          apiResponse[`field_name`],
          apiResponse[`field_length`],
          apiResponse[`max_length`],
        );
      case PermissionDeniedError.type:
        throw new PermissionDeniedError(apiResponse.error);
      case NotImplementedError.type:
        throw new NotImplementedError(apiResponse.error);
      case InvalidParamsError.type:
        throw new InvalidParamsError(apiResponse.error);
      case DashboardDoesNotExistError.type:
        throw new DashboardDoesNotExistError(apiResponse.error);
      case CardDoesNotExistError.type:
        throw new CardDoesNotExistError(apiResponse.error);
      case CardOrderOutOfSyncError.type:
        throw new CardOrderOutOfSyncError(apiResponse.error);
      case BookmarkDoesNotExistError.type:
        throw new BookmarkDoesNotExistError(apiResponse.error);
    }
  }

  processResponse(response) {
    if (response.error) {
      this.throwApiError(response);
    }

    // TODO: untangle BaseClient and use apiClient directly
    // BaseClient uses apiClient.fetch which returns top level object
    // apiClient.(get|put|post|patch|delete) return the value of response.results
    // we should use move away from using apiClient.fetch directly unless the server response
    // is not conformant to the api response format
    if (Object.prototype.hasOwnProperty.call(response, `results`)) {
      return response.results;
    }

    return response;
  }

  async invalidateFetchCache(): Promise<void> {
    // this gets us out of trouble for now, but in the future Dashboards should migrate to Store
    await purgeFetchCachePathsMatchingRegex(/^\/api\/app\/(?:workspaces|projects)\/\d+\/dashboard.*/);
  }

  async updateReportLink({reportLinkId, projectId, workspaceId, dashboardID, updateData}) {
    const url = this.buildEndpoint({projectId, dashboardID, workspaceId, reportLinkId});
    await apiClient.patch(url, updateData);
  }

  //TODO @shayan or @gonzalo remove this and just use addNewContentToDashboard after GA
  async addNewDashboardBookmark({dashboardOrigin = null, bookmarkData, projectId, workspaceId, dashboardId}) {
    const contentType = ContentType.Report;

    const gridDetails = dashboardOrigin?.grid_details;

    const contentParams = {
      bookmark: {...omit(bookmarkData, `id`, `prev_bookmark_url_slug`), dashboard_id: dashboardId},
    };

    const bookmark = await this.addNewContentToDashboard({
      gridDetails,
      projectId,
      workspaceId,
      dashboardId,
      contentDetails: {contentParams, contentType},
    });

    return bookmark;
  }

  async addNewContentToDashboard({
    gridDetails = null,
    contentDetails,
    dashboardId,
    projectId,
    workspaceId,
  }: {
    gridDetails?: any;
    contentDetails: ContentDetails;
    dashboardId: number;
    projectId: any;
    workspaceId: any;
  }) {
    const newCell = {cellId: `-1`, colSpan: MAX_CELL_COL_SPAN};

    let rowWithAddedCell;

    if (gridDetails) {
      const {row} = gridDetails;
      const updatedCellItems = addNewCellToRow(row.cellItems, newCell, 0);
      rowWithAddedCell = {...row, cellItems: updatedCellItems};
    }

    const fields = {
      ...(rowWithAddedCell ? {layout: getLayoutUpdateParams([rowWithAddedCell], gridDetails.rowsOrder)} : {}),
      content: {
        action: ContentActionType.Create,
        content_type: contentDetails.contentType,
        content_params: contentDetails.contentParams,
      },
    };

    const {new_content: newContent} = await this.updateDashboardFields({
      projectId,
      workspaceId,
      dashboardID: dashboardId,
      fields,
    });

    return newContent;
  }

  //TODO @shayan or @gonzalo remove this and just use deleteDashboardContent after GA
  async removeDashboardBookmark({bookmark, projectId, workspaceId}) {
    const dashboardId = bookmark?.dashboard_id || bookmark.dashboard?.id;
    const bookmarkId = bookmark.id;
    const loadDashboard = this.querySingle({projectId, workspaceId, dashboardID: dashboardId});
    const dashboard = await loadDashboard.network;

    const content = {contentId: bookmarkId, contentType: ContentType.Report};

    await this.deleteDashboardContent({gridLayout: dashboard.layout, content, projectId, workspaceId, dashboardId});
  }

  async deleteDashboardContent({gridLayout, content, projectId, workspaceId, dashboardId}) {
    const layoutPatch = getLayoutParamWithContentRemoved(content, gridLayout);
    const fields = {
      ...(layoutPatch ? {layout: layoutPatch} : {}),
      content: {
        action: ContentActionType.Delete,
        content_type: content.contentType,
        content_id: content.contentId,
      },
    };

    const dashboard = await this.updateDashboardFields({
      projectId,
      workspaceId,
      dashboardID: dashboardId,
      fields,
    });
    return dashboard;
  }

  async nestDashboard({projectId, workspaceId, dashboard, parentDashboardId}) {
    // we are using "unmove"/"moveTo", so we set the new parent dashboard id into the URL
    const url = this.buildEndpoint({projectId, dashboardID: parentDashboardId, workspaceId});

    const fields = {
      content: {
        action: ContentActionType.UnMove,
        content_type: ContentType.Dashboard,
        content_id: dashboard.id,
      },
    };
    const result = await apiClient.patch<Dashboard>(url, fields);
    return result;
  }

  async unnestDashboard({projectId, workspaceId, dashboard, parentDashboardId}) {
    // we are using "move"/"moveFrom", so we set the old parent dashboard id into the URL
    const url = this.buildEndpoint({projectId, dashboardID: parentDashboardId, workspaceId});

    const fields = {
      content: {
        action: ContentActionType.Move,
        content_type: ContentType.Dashboard,
        content_id: dashboard.id,
        content_params: {
          target_dashboard_id: undefined,
        },
      },
    };
    const result = await apiClient.patch<Dashboard>(url, fields);
    return result;
  }
}
export type DashboardsClient = InstanceType<typeof DashClient>;
export const dashboardsClient = new DashClient();
